<script lang="ts">
  import { omitBy, clone } from "lodash";
  import { params, goto } from "@roxi/routify";

  // services
  import {
    getQuickOrderListDetail,
    getQuickOrderListIndex,
    getQuickOrderListProducts,
    prepareFilters,
  } from "@shared/services/list_service";
  import { getDistributors } from "@modern/services/distributors";
  import { getProductTypes } from "@modern/services/product_types";
  import { getStateSettings } from "@shared/services/state_settings_service";
  import { getListBanners } from "@shared/services/banner_service";

  // components
  import ListBuilderWrapper from "@modern/components/retailer/ListBuilder/ListBuilderWrapper.svelte";
  import ListDetailPage from "@retailer/svelte/Shared/QuickOrderList/Page.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  // stores
  import QuickOrderListProductsStore from "@retailer/svelte/stores/QuickOrderListProductsStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  // types
  import type { Distributor } from "@shared/types/Distributor";
  import type { PageData } from "@shared/types/PageData";
  import type {
    QuickOrderListDetail,
    QuickOrderListFilters,
    QuickOrderListIndex,
  } from "@shared/types/QuickOrderList";
  import type {
    Category,
    Container,
    ProductLine,
    ProductType,
    QuickOrderListProduct,
  } from "@shared/types/ProductLine";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  if (!$params.listId) $goto("/lists");

  type LoadData = {
    distributors: Distributor[];
    fulfillmentMethods?: Record<number, RetailerFulfillmentMethod>;
    list: QuickOrderListDetail;
    lists?: QuickOrderListIndex[];
    quickOrderListProducts?: QuickOrderListProduct[];
    productTypes: {
      categories: Category[];
      containers: Container[];
      types: ProductType[];
    };
  };

  const callbacks: {
    closeListBuilder: (changedList: boolean) => Promise<void>;
    onChooseSizeForProductLinePlaceholder: (
      productLine: ProductLine,
      quickOrderListProductId: number,
    ) => void;
    openListBuilder: () => void;
  } = {
    closeListBuilder: async changedList => {
      showListBuilder = false;

      if (changedList) {
        data = Object.assign(data, await reload(clone($params)));
      }
    },
    onChooseSizeForProductLinePlaceholder: (productLine, quickOrderListProductId) => {
      listBuilderScope.initialStateForListBuilder();
      listBuilderScope.listBuilderState.placeholderProduct = { id: quickOrderListProductId };
      listBuilderScope.onChooseProductSize(productLine);
      callbacks.openListBuilder();
    },
    openListBuilder: () => {
      showListBuilder = true;
    },
  };

  let data: LoadData;
  let filters: QuickOrderListFilters;
  let listBuilderScope: any = {};
  let page: PageData;
  let showListBuilder = false;

  const authService = getControlStateAuthenticationService();

  const filter = async (event: CustomEvent): Promise<void> => {
    let tempStateParams = clone($params);

    if (event) tempStateParams = Object.assign(tempStateParams, event.detail.filters);
    if (tempStateParams["deleted"] === null) delete tempStateParams["deleted"];

    $goto(
      "/lists/" + tempStateParams.listId,
      omitBy(tempStateParams, param => param === null || param === undefined || param === "null"),
    );
  };

  const getPageData = (params: Record<string, any>): PageData => {
    const savedSort = JSON.parse(window.localStorage.getItem(`SortPref-${params.listId}`)) || {};

    return {
      current: "layout.list_details",
      params: { ...params, ...savedSort },
      sortOrder: null,
    };
  };

  const getParamDependentData = (
    params: Record<string, any>,
  ): [
    Promise<Distributor[]>,
    Promise<QuickOrderListDetail>,
    Promise<LoadData["productTypes"]>,
    Promise<QuickOrderListProduct[]>,
  ] => {
    const savedSort = JSON.parse(window.localStorage.getItem(`SortPref-${params.listId}`));

    return [
      getDistributors({
        filtered: true,
        list_id: params.listId,
        with_integrated: true,
      }),
      getQuickOrderListDetail(params.listId, params),
      getProductTypes({
        filtered: true,
        quick_order_list_ids: [params.listId],
      }),
      getQuickOrderListProducts({
        ...params,
        direction: params.direction || savedSort?.direction,
        page: params.page || 1,
        sort: params.sort || savedSort?.sort,
      }),
    ];
  };

  async function load(params: Record<string, any>): Promise<LoadData> {
    const [
      lists,
      stateSettings,
      _fulfillmentMethods,
      distributors,
      list,
      productTypes,
      quickOrderListProducts,
    ] = await Promise.all([
      getQuickOrderListIndex(),
      getStateSettings(),
      RetailerFulfillmentMethodsStore.refreshFulfillmentMethods(),
      ...getParamDependentData(params),
    ]);

    if (list) list.quick_order_list_products = quickOrderListProducts;

    QuickOrderListProductsStore.set(quickOrderListProducts);
    StateSettingsStore.set(stateSettings);
    page = getPageData(params);
    filters = prepareFilters({ distributors, productTypes });
    const listType = list.list_type;
    await getListBanners(listType, filters, params, quickOrderListProducts?.length || 0);

    return {
      distributors,
      list,
      lists,
      productTypes,
    };
  }

  async function reload(params: Record<string, any>): Promise<LoadData> {
    const [distributors, list, productTypes, quickOrderListProducts] = await Promise.all(
      getParamDependentData(params),
    );

    if (list) list.quick_order_list_products = quickOrderListProducts;

    QuickOrderListProductsStore.set(quickOrderListProducts);
    page = getPageData(params);
    filters = prepareFilters({ distributors, productTypes });
    const listType = list.list_type;
    getListBanners(listType, filters, params, quickOrderListProducts?.length || 0);

    return {
      distributors,
      list,
      productTypes,
    };
  }

  async function fetchData(params: Record<string, any>): Promise<void> {
    if (data) data = Object.assign(data, await reload(params));
    else data = await load(params);
  }

  $: fetchData(clone($params));

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  $: {
    if (hasRetailerUserAuthRollout && filters && filters.distributors) {
      let filteredDistributors = filters.distributors.filter(
        d => !shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, [d.id]),
      );
      filters.distributors = filteredDistributors;
    }
  }
</script>

{#if data}
  <ListDetailPage {callbacks} {filter} {page} list={data.list} lists={data.lists} bind:filters />
  <ListBuilderWrapper
    {callbacks}
    {showListBuilder}
    bind:list={data.list}
    bind:scope={listBuilderScope}
  />
{/if}

{#if authService?.authenticationModalComponent}
  <svelte:component this={authService?.authenticationModalComponent} />
{/if}
